// ============================================================
// Anchor-positioning feature detection + ID minting — TWS-122
// ------------------------------------------------------------
// Cross-primitive helpers consumed by `tw-dropdown`, `tw-tooltip`,
// and `tw-popover`. All primitives share:
//
//   - A feature-detect for CSS anchor positioning so Wave 3 can
//     fall back to CDK Overlay's `FlexibleConnectedPositionStrategy`
//     when native support is missing.
//   - A feature-detect for `popover="hint"` specifically (tooltips
//     prefer it, but it ships later than the base `[popover]` API).
//   - Per-instance unique anchor-name and popover-id generators so
//     multiple instances on the same page don't collide.
//
// The detects memoize their result on first call — each probe
// touches the CSSOM / prototype once per process and the answer
// cannot change at runtime. Tests can reset the cache via
// `__resetSupportCacheForTesting()` (test-only, not exported from
// the lib barrel).
// ============================================================

// ---------------------------------------------------------------
// Feature detection
// ---------------------------------------------------------------

let anchorSupportCache: boolean | null = null;
let popoverHintSupportCache: boolean | null = null;
let popoverApiSupportCache: boolean | null = null;

/**
 * True iff the browser supports CSS anchor positioning
 * (`anchor-name`, `position-anchor`, `position-try-fallbacks`).
 * Nearly-Baseline as of mid-2026: Chrome/Edge 125+, Safari 26.1+,
 * Firefox 149+.
 *
 * Memoized on first call — the result cannot change at runtime
 * without a browser upgrade, and probing the CSSOM repeatedly adds
 * noise to the hot paths that run on every open.
 */
export function supportsAnchorPositioning(): boolean {
  if (anchorSupportCache !== null) return anchorSupportCache;

  // SSR / non-browser — treat as unsupported. Consumers degrade to
  // the CDK fallback (wired in Wave 3).
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    anchorSupportCache = false;
    return anchorSupportCache;
  }

  // `CSS.supports(property, value)` returns true iff the browser
  // would accept the declaration. `anchor-name: --probe` is the
  // cheapest per-property check; `position-try-fallbacks` is
  // coupled to it in spec-supporting browsers so one probe is
  // sufficient.
  anchorSupportCache = CSS.supports('anchor-name: --probe');
  return anchorSupportCache;
}

/**
 * True iff the browser supports `popover="hint"` (the tooltip
 * variant that auto-dismisses when another hint opens). Later than
 * the base `[popover]` API: Chrome 134+, Safari 26.1+, Firefox 149+.
 *
 * The tooltip primitive (Wave 2) prefers `"hint"` for its
 * auto-dismiss-on-next-hint semantics and falls back to `"auto"`
 * with manual dismiss when unsupported.
 *
 * Memoized. Detects via two signals:
 *   1. `HTMLElement.prototype.popover` exists (base `[popover]`
 *      API — a prerequisite for the hint variant);
 *   2. Setting `popover="hint"` on a detached element and reading
 *      back the reflected property value matches `"hint"`.
 *
 * Older browsers that implement base `[popover]` but not the hint
 * variant coerce the value to `"auto"` on set, so the round-trip
 * check distinguishes them cleanly.
 */
export function supportsPopoverHint(): boolean {
  if (popoverHintSupportCache !== null) return popoverHintSupportCache;

  if (typeof HTMLElement === 'undefined') {
    popoverHintSupportCache = false;
    return popoverHintSupportCache;
  }

  // Base `[popover]` attribute reflects via the `popover` IDL
  // property on HTMLElement. Missing property === feature absent.
  if (!('popover' in HTMLElement.prototype)) {
    popoverHintSupportCache = false;
    return popoverHintSupportCache;
  }

  try {
    const probe = document.createElement('div');
    probe.setAttribute('popover', 'hint');
    // The property reflects the attribute after setAttribute. If
    // the browser doesn't know the `hint` value, the reflected
    // property coerces to `'auto'` (the spec's default).
    popoverHintSupportCache = probe.popover === 'hint';
  } catch {
    // Exotic environment (JSDOM without the API, etc.).
    popoverHintSupportCache = false;
  }

  return popoverHintSupportCache;
}

/**
 * True iff the base `[popover]` API is present — that means
 * `HTMLElement.prototype.popover`, `showPopover()`, `hidePopover()`,
 * and `togglePopover()` are all wired up. Narrower than
 * `supportsPopoverHint()` (which additionally requires the `hint`
 * variant); this helper tells us whether the "native open / close"
 * fast path is available at all.
 *
 * Used by `getPositioningStrategy()` (see `positioning-strategy.ts`)
 * to decide whether to take the CDK Overlay fallback. Memoized —
 * the answer can't change without a browser upgrade.
 *
 * JSDOM note: Jest's JSDOM environment does NOT wire these methods
 * up, so this detect returns `false` in unit tests unless the
 * `installPopoverPolyfill()` helper (see
 * `shared/testing/popover-polyfill.ts`) has been installed first.
 * Our `test-setup.ts` installs the polyfill automatically, so the
 * detect returns `true` in tests after setup runs.
 */
export function supportsPopoverApi(): boolean {
  if (popoverApiSupportCache !== null) return popoverApiSupportCache;

  if (typeof HTMLElement === 'undefined') {
    popoverApiSupportCache = false;
    return popoverApiSupportCache;
  }

  // All four IDL surfaces must be present. Method presence on the
  // prototype is the safe check — browsers that ship the attribute
  // but not the methods (never happened in practice, but cheap to
  // guard) would be rejected correctly.
  const proto = HTMLElement.prototype as unknown as {
    showPopover?: () => void;
    hidePopover?: () => void;
    togglePopover?: () => void;
  };
  popoverApiSupportCache =
    'popover' in HTMLElement.prototype &&
    typeof proto.showPopover === 'function' &&
    typeof proto.hidePopover === 'function' &&
    typeof proto.togglePopover === 'function';
  return popoverApiSupportCache;
}

// ---------------------------------------------------------------
// Per-instance ID minting
// ---------------------------------------------------------------
//
// Anchor-name and popover-id uniqueness: CSS custom properties with
// the same `--name` from two dropdowns would make both menus try
// to anchor to whichever trigger was painted last. Monotonic
// counters keep the IDs process-unique and cheap.

let anchorCounter = 0;
let popoverCounter = 0;

/**
 * Return a process-unique CSS custom-property name for anchor
 * wiring — e.g. `--twist-anchor-7`. Consumers bind the same name
 * to the trigger via `anchor-name: var(--twist-anchor-7)` and
 * reference it from the menu via `position-anchor: --twist-anchor-7`.
 *
 * The prefix intentionally matches the `--twist-*` namespace so
 * DevTools searches for Twist-owned custom properties surface them.
 */
export function createAnchorName(): string {
  anchorCounter += 1;
  return `--twist-anchor-${anchorCounter}`;
}

/**
 * Return a process-unique DOM id for the popover element —
 * e.g. `tw-popover-3`. Used for `popovertarget="tw-popover-3"` on
 * the trigger + matching `id="tw-popover-3"` on the menu.
 */
export function createPopoverId(): string {
  popoverCounter += 1;
  return `tw-popover-${popoverCounter}`;
}

// ---------------------------------------------------------------
// Test-only escape hatch
// ---------------------------------------------------------------

/**
 * Reset the memoized support caches so a spec can feature-detect
 * under different simulated environments (e.g. stub `CSS.supports`
 * to return `false` and verify the fallback path triggers). NOT
 * exported from the public `index.ts` — pure testing affordance.
 *
 * @internal
 */
export function __resetSupportCacheForTesting(): void {
  anchorSupportCache = null;
  popoverHintSupportCache = null;
  popoverApiSupportCache = null;
}
